import { useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { ShieldCheck, Eye, EyeOff, Info, ArrowRight, Lock } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { useAuth, findCdmsAccess } from "@/lib/auth";
import type { AuthMemberModel } from "@/types/authType";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import ConfirmDialog from "@/components/ui/confirm-dialog";
import ForgotPasswordModal from "@/routes/01_index/forgot-password-modal";
import SetNewPasswordModal from "@/routes/01_index/new-password-modal";
import { Input } from "@/components/ui/input";
import { authAPI } from "@/services/authAPI";

function Login() {
  const { user, login, initialized } = useAuth();

  const navigate = useNavigate();
  const [badge, setBadge] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [forgotInputOpen, setForgotInputOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [apiMember, setApiMember] = useState<AuthMemberModel | null>(null);

  useEffect(() => {
    if (initialized && user) navigate("/dashboard");
  }, [initialized, user, navigate]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!badge || !password) {
      toast.error("Badge number and password are required");
      return;
    }
    setPending(true);
    authAPI
      .login({ badgeno: badge, userpass: password } as any)
      .then((resp) => {
        const body: any = resp?.data ?? resp;
        if (!body) {
          toast.error("Unable to complete login at this time. Please try again later.");
          return;
        }
        if (!body.isSuccess) {
          // Invalid credentials or business-level rejection from the server.
          toast.error(
            "Invalid badge number or password. Please verify your credentials and try again.",
          );
          return;
        }
        if (body.member && body.member.isnewaccount) {
          // First-time login: force password reset before any access check.
          setApiMember(body.member);
          setResetOpen(true);
          return;
        }
        if (!body.member || !body.accessToken) {
          toast.error("Unable to complete login at this time. Please try again later.");
          return;
        }

        // CDMS system-access validation — the single source of authorization.
        const cdms = findCdmsAccess(body.member);
        if (!cdms) {
          toast.error(
            "Your account is not configured for this system. Please contact your administrator to request access.",
          );
          return;
        }
        if (!cdms.hasaccess) {
          toast.error(
            "Access denied. Your account does not have permission to access the Centralized Data Management System. Please contact your administrator.",
          );
          return;
        }

        login({
          member: body.member,
          accessToken: body.accessToken,
          expiration: body.expiration ?? "",
          systemAccess: cdms,
        });
        navigate("/dashboard");
      })
      .catch((err) => {
        console.error("Login error", err);
        const status = err?.status ?? err?.response?.status ?? 0;
        if (status === 401) {
          toast.error(
            "Invalid badge number or password. Please verify your credentials and try again.",
          );
        } else if (status === 403) {
          toast.error(
            "Your account does not have access to this system. Please contact your administrator.",
          );
        } else {
          toast.error("Unable to complete login at this time. Please try again later.");
        }
      })
      .finally(() => setPending(false));
  };


  return (
    <div className="min-h-screen w-full" style={{ background: "var(--gradient-subtle)" }}>
      <div className="mx-auto grid min-h-screen max-w-6xl grid-cols-1 items-center gap-8 p-6 lg:grid-cols-2">
        {/* Brand panel */}
        <div className="hidden flex-col gap-6 lg:flex">
          <div className="flex items-center gap-3">
            <div
              className="flex h-12 w-12 items-center justify-center rounded-xl text-primary-foreground"
              style={{ background: "var(--gradient-primary)", boxShadow: "var(--shadow-glow)" }}
            >
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">CDMS</h1>
              <p className="text-sm text-muted-foreground">Centralized Data Management System</p>
            </div>
          </div>

          <h2 className="text-4xl font-bold leading-tight tracking-tight">
            Centralized data management
            <br />
            <span className="text-primary">for the Bureau of Fire Protection.</span>
          </h2>
          <p className="max-w-md text-muted-foreground">
            One unified workspace for personnel, stations, offices, and locations across the
            MIMAROPA Region.
          </p>

          <div className="grid grid-cols-2 gap-3">
            {["Secure login", "Role-Based Access", "Centralized Records", "BFP Standard"].map(
              (label) => (
                <div key={label} className="flex items-center gap-2 rounded-lg border bg-card p-3">
                  <ShieldCheck className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">{label}</span>
                </div>
              ),
            )}
          </div>
        </div>

        {/* Login card */}
        <Card className="p-6 shadow-xl">
          <div className="mb-6 flex items-center gap-3 lg:hidden">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-lg font-bold">CDMS — BFP MIMAROPA</h1>
            </div>
          </div>

          <h3 className="text-2xl font-semibold flex items-center gap-2">
            <Lock className="hidden lg:inline-flex h-5 w-5 text-primary" />
            Welcome to CDMS
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Sign in with your badge number and password to access the dashboard.
          </p>

          <form className="mt-6" onSubmit={handleSubmit} noValidate>
            <TooltipProvider>
              <div className="grid gap-3">
                <div>
                  <label className="flex items-center justify-between text-sm mb-2">
                    <span className="font-medium">Badge number</span>
                  </label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Input
                        name="username"
                        value={badge}
                        onChange={(e) => setBadge(e.target.value)}
                        placeholder="e.g. 12345"
                        className="h-12 rounded-lg border px-3"
                        required
                        autoComplete="username"
                      />
                    </TooltipTrigger>
                    {!badge && (
                      <TooltipContent side="top">Please fill out this field</TooltipContent>
                    )}
                  </Tooltip>
                </div>

                <div>
                  <label className="flex items-center justify-between text-sm mb-2">
                    <span className="font-medium">Password</span>
                  </label>

                  <div className="relative">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Input
                          name="password"
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Your password"
                          className="h-12 rounded-lg pr-10 px-3"
                          required
                          autoComplete="current-password"
                        />
                      </TooltipTrigger>
                      {!password && (
                        <TooltipContent side="top">Please fill out this field</TooltipContent>
                      )}
                    </Tooltip>
                    <button
                      type="button"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      onClick={() => setShowPassword((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <Button
                    type="submit"
                    className="w-full sm:w-auto h-12 inline-flex items-center gap-3 rounded-lg px-4 py-2 bg-gradient-to-r from-primary to-primary/80 text-white shadow-md hover:shadow-lg transition-shadow"
                    disabled={!badge || !password || pending}
                  >
                    {pending ? (
                      <span className="inline-flex items-center gap-2">
                        <svg
                          className="h-4 w-4 animate-spin"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="color-mix(in oklab, var(--primary-foreground) 30%, transparent)"
                            strokeWidth="4"
                          />
                          <path
                            d="M22 12a10 10 0 0 0-10-10"
                            stroke="var(--primary-foreground)"
                            strokeWidth="4"
                            strokeLinecap="round"
                          />
                        </svg>
                        Signing in...
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        Sign in
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-sm text-muted-foreground"
                    onClick={() => setForgotInputOpen(true)}
                  >
                    Forgot password?
                  </Button>
                </div>
              </div>
            </TooltipProvider>
          </form>

          {/* Input modal: prompt for email or badge number */}
          <ForgotPasswordModal
            open={forgotInputOpen}
            onOpenChange={setForgotInputOpen}
            onSend={async (value: string) => {
              // parent does not need to manage the post-send notice; modal will
              // show the informational dialog after sending.
              return;
            }}
          />

          {/* Notice dialog moved into ForgotPasswordModal */}

          <SetNewPasswordModal
            open={resetOpen}
            onOpenChange={(v) => {
              setResetOpen(v);
              if (!v) setApiMember(null);
            }}
            member={apiMember ?? undefined}
            onUpdated={() => setApiMember(null)}
          />
        </Card>
      </div>
    </div>
  );
}

export default function RouteComponent() {
  useEffect(() => {
    document.title = "Sign In — CDMS — Bureau of Fire Protection MIMAROPA";
  }, []);
  return <Login />;
}
